import { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Phone, CheckCircle2, AlertTriangle, Loader2, Eye, EyeOff, Sparkles } from "lucide-react";

/**
 * One-screen Twilio config so recruiters never see Twilio's HTTP Basic Auth
 * popup when streaming phone-call recordings.
 *
 * UX model mirrors StorageWizard:
 *  - "Use default settings" is checked by default when no custom Twilio has
 *    been saved. The default panel shows the platform-default caller number.
 *  - Unchecking reveals the full custom credentials form.
 */
export default function TwilioWizard() {
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({ account_sid: "", auth_token: "", phone_number: "" });
  const [showSecret, setShowSecret] = useState(false);
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [useDefaults, setUseDefaults] = useState(null);
  const [switching, setSwitching] = useState(false);
  const debounceRef = useRef(null);
  const seqRef = useRef(0);

  const load = async () => {
    try {
      const { data } = await api.get("/twilio");
      setCurrent(data);
      setUseDefaults(data.is_platform_owner ? false : !data.custom);
    } catch {
      setCurrent({ configured: false, custom: false, using_defaults: false, defaults_available: false, is_platform_owner: false });
      setUseDefaults(true);
    }
  };
  useEffect(() => { load(); }, []);

  const canProbe = form.account_sid.trim() && form.auth_token.trim();

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (useDefaults || !canProbe) { setValidation(null); return; }
    debounceRef.current = setTimeout(() => doValidate(), 500);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [form.account_sid, form.auth_token, form.phone_number, useDefaults]);

  const doValidate = async () => {
    const seq = ++seqRef.current;
    setValidating(true);
    try {
      const { data } = await api.post("/twilio/validate", form);
      if (seq !== seqRef.current) return;
      setValidation(data);
    } catch (err) {
      if (seq !== seqRef.current) return;
      setValidation({ ok: false, code: "network", message: fmtErr(err.response?.data?.detail) || "Validation failed" });
    } finally {
      if (seq === seqRef.current) setValidating(false);
    }
  };

  const save = async () => {
    if (!validation?.ok) { toast.error("Fix the validation errors before saving."); return; }
    setSaving(true);
    try {
      await api.post("/twilio", form);
      toast.success("Twilio configured — phone-call recordings will play inline.");
      setForm({ ...form, auth_token: "" });
      load();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const switchToDefaults = async () => {
    if (!window.confirm("Switch back to the platform's default Twilio account? New candidate calls will use that number.")) return;
    setSwitching(true);
    try {
      await api.delete("/twilio");
      toast.success("Switched to default phone settings.");
      setForm({ account_sid: "", auth_token: "", phone_number: "" });
      setValidation(null);
      load();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Could not switch back");
      load();
    } finally { setSwitching(false); }
  };

  const onToggleDefaults = (next) => {
    if (next === false) { setUseDefaults(false); return; }
    if (current?.custom) { switchToDefaults(); return; }
    setUseDefaults(true);
  };

  const hint = useMemo(() => {
    if (!validation || validation.ok) return null;
    if (validation.code === "bad_auth") return "Open your Twilio Console → Account → Account Info and copy the Auth Token for this exact Account SID (not another sub-account).";
    if (validation.code === "bad_sid") return "SIDs look like `ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx` (34 chars, starts with 'AC').";
    if (validation.code === "bad_phone") return "The number must be an active Twilio number owned by this account, in E.164 format (e.g. +15551234567).";
    return null;
  }, [validation]);

  if (useDefaults === null) {
    return (
      <div className="tp-card p-6" data-testid="twilio-wizard-loading">
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin"/> Loading phone settings…</div>
      </div>
    );
  }

  return (
    <div className="tp-card p-6" data-testid="twilio-wizard">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-indigo-500"/>
            <div className="font-display text-lg font-semibold">Phone Integration (Twilio)</div>
            {current?.is_platform_owner && <span className="tp-chip tp-chip-amber" data-testid="twilio-status-po"><Sparkles className="w-3 h-3 inline mr-1"/>Platform default</span>}
            {!current?.is_platform_owner && current?.using_defaults && <span className="tp-chip tp-chip-indigo" data-testid="twilio-status-default"><Sparkles className="w-3 h-3 inline mr-1"/>Using defaults</span>}
            {current?.custom && !current?.is_platform_owner && <span className="tp-chip tp-chip-emerald" data-testid="twilio-status-ok"><CheckCircle2 className="w-3 h-3 inline mr-1"/>Custom</span>}
            {current && !current.configured && !current?.is_platform_owner && <span className="tp-chip tp-chip-amber" data-testid="twilio-status-missing"><AlertTriangle className="w-3 h-3 inline mr-1"/>Not configured</span>}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {current?.is_platform_owner
              ? "The Twilio account you save here becomes the default caller for every tenant that hasn't connected their own."
              : "Powers the AI telephonic round. New workspaces use the platform's shared Twilio account — opt out below to dial candidates from your own Twilio number."}
          </div>
        </div>
      </div>

      {!current?.is_platform_owner && (
        <label className="flex items-start gap-3 p-3 mb-4 rounded-lg bg-indigo-50 border border-indigo-200 cursor-pointer" data-testid="twilio-use-defaults-row">
          <input
            type="checkbox"
            data-testid="twilio-use-defaults"
            checked={useDefaults}
            disabled={switching}
            onChange={(e) => onToggleDefaults(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-indigo-600"
          />
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
              Use default settings
              {switching && <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-500"/>}
            </div>
            <div className="text-xs text-slate-600">
              Use the platform's shared Twilio number{current?.defaults_source === "platform_owner" ? " (set by your Platform Owner)" : ""}. Uncheck to bring your own Twilio account.
            </div>
          </div>
        </label>
      )}

      {useDefaults && !current?.is_platform_owner ? (
        <div className="p-4 rounded-lg bg-white border border-slate-200 text-xs space-y-1" data-testid="twilio-defaults-panel">
          {current?.defaults_available ? (
            <>
              <div><span className="text-slate-500 uppercase tracking-wider mr-2">Caller ID</span><span className="font-mono text-slate-800">{current.default_phone_number || "(platform assigns on each call)"}</span></div>
              <div><span className="text-slate-500 uppercase tracking-wider mr-2">Account</span><span className="font-mono text-slate-800">{current.default_account_sid_masked}</span></div>
              <div className="text-[11px] text-slate-500 pt-1">Candidates will see this number ring when the AI screener dials them.</div>
            </>
          ) : (
            <div className="flex items-start gap-2 text-amber-700">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0"/>
              <div>
                <div className="font-medium">Platform default Twilio isn't configured yet.</div>
                <div className="text-[11px] text-amber-700/80 mt-0.5">Ask your Emergent admin to set <code>TWILIO_*</code> env vars, or uncheck above to add your own account now.</div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="grid gap-3">
          {current?.custom && (
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600 space-y-0.5" data-testid="current-twilio">
              <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1">Currently saved</div>
              <div><span className="text-slate-500 uppercase tracking-wider mr-2">Account SID</span>{current.account_sid_masked}</div>
              <div><span className="text-slate-500 uppercase tracking-wider mr-2">Twilio Number</span>{current.phone_number || "(not set — calls will be rejected)"}</div>
              {current.last_validated_at && <div><span className="text-slate-500 uppercase tracking-wider mr-2">Validated</span>{new Date(current.last_validated_at).toLocaleString()}</div>}
            </div>
          )}
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider">Account SID</label>
            <input
              data-testid="twilio-sid"
              value={form.account_sid}
              onChange={(e) => setForm({ ...form, account_sid: e.target.value.trim() })}
              placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              className={`w-full mt-1 px-3 py-2 rounded-lg bg-white border font-mono text-sm outline-none ${
                validation?.ok ? "border-emerald-500/50" :
                validation && !validation.ok ? "border-rose-500/50" :
                "border-slate-300 focus:border-indigo-500"
              }`}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider">Auth Token</label>
            <div className="relative mt-1">
              <input
                data-testid="twilio-token"
                type={showSecret ? "text" : "password"}
                value={form.auth_token}
                onChange={(e) => setForm({ ...form, auth_token: e.target.value.trim() })}
                placeholder="your_32_char_auth_token"
                className="w-full px-3 py-2 pr-10 rounded-lg bg-white border border-slate-300 focus:border-indigo-500 outline-none font-mono text-sm"
              />
              <button type="button" onClick={() => setShowSecret(s => !s)} data-testid="toggle-twilio-secret" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700">
                {showSecret ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider">Twilio Phone Number (E.164)</label>
            <input
              data-testid="twilio-number"
              value={form.phone_number}
              onChange={(e) => setForm({ ...form, phone_number: e.target.value.trim() })}
              placeholder="+15551234567"
              className="w-full mt-1 px-3 py-2 rounded-lg bg-white border border-slate-300 focus:border-indigo-500 outline-none font-mono text-sm"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Must be a number owned by this Twilio account. We dial candidates from here.
            </div>
          </div>

          <div data-testid="twilio-validation-banner" aria-live="polite">
            {validating && (
              <div className="flex items-center gap-2 text-xs text-slate-500 p-3 rounded-lg bg-slate-50 border border-slate-200">
                <Loader2 className="w-3.5 h-3.5 animate-spin"/> Validating credentials…
              </div>
            )}
            {!validating && validation?.ok && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                <div>
                  <div className="font-medium">{validation.message}</div>
                </div>
              </div>
            )}
            {!validating && validation && !validation.ok && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-sm">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                <div>
                  <div className="font-medium">{validation.message}</div>
                  {hint && <div className="text-xs text-rose-700/80 mt-1">{hint}</div>}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              data-testid="twilio-save"
              onClick={save}
              disabled={!validation?.ok || saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle2 className="w-4 h-4"/>}
              Save custom credentials
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
